import { defineStore } from "pinia";
import { ref } from "vue";
import { comparisonDB } from "./comparison.db";
import type { ComparisonRecord } from "./comparison.types";

// Strip Vue reactivity before handing the record to IndexedDB — structured
// clone rejects Proxy-wrapped objects, and our payload is plain JSON-shaped
// data, so a JSON round-trip gives a safe, detached copy.
function toPlain<T>(value: T): T {
  return value === null || value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T);
}

export const useComparisonStore = defineStore("comparison", () => {
  // Newest-first list of saved comparisons, mirrored from IndexedDB for the UI.
  const items = ref<ComparisonRecord[]>([]);
  const loaded = ref(false);

  // Hydrate from IndexedDB once; safe to call repeatedly.
  async function load() {
    if (loaded.value) return;
    items.value = await comparisonDB.comparisons.orderBy("id").reverse().toArray();
    loaded.value = true;
  }

  function get(id: number): ComparisonRecord | undefined {
    return items.value.find((item) => item.id === id);
  }

  // Persist a new comparison (id is assigned by IndexedDB) and prepend it.
  async function add(record: Omit<ComparisonRecord, "id">): Promise<number> {
    const plain = toPlain(record);
    const id = await comparisonDB.comparisons.add(plain as unknown as ComparisonRecord);
    items.value = [{ ...plain, id }, ...items.value];
    return id;
  }

  // Patch an existing comparison (e.g. its `display` settings) and persist.
  async function update(id: number, patch: Partial<ComparisonRecord>): Promise<void> {
    const existing = get(id);
    if (!existing) return;
    const entry = toPlain({ ...existing, ...patch });
    await comparisonDB.comparisons.put(entry);
    items.value = items.value.map((item) => (item.id === id ? entry : item));
  }

  async function remove(id: number) {
    await comparisonDB.comparisons.delete(id);
    items.value = items.value.filter((item) => item.id !== id);
  }

  return { items, loaded, load, get, add, update, remove };
});
